import React, { useMemo, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import { Box } from '@mui/material';

import DataTableIcon from '@/assets/icons/DataTable';
import FWDChartIcon from '@/assets/icons/FWDChart';
import HighlightPriceIcon from '@/assets/icons/HighlightPrice';
import SimpleChartIcon from '@/assets/icons/SimpleChart';
import Button from '@/components/Buttons/Button';
import Typography from '@/components/Typography';
import SelectWidget from '@/features/Dashboard/components/SelectWidget';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { DrawerBody } from '@/features/Dashboard/drawer/DrawerBody';
import { DrawerButton } from '@/features/Dashboard/drawer/DrawerButton';
import { DrawerHeader } from '@/features/Dashboard/drawer/DrawerHeader';
import useUserDetails from '@/hooks/useUserDetails';

enum Category {
  All = 'All',
  Markets = 'Markets',
  Charts = 'Charts',
  Data = 'Data'
}
interface App {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: Category;
  step: DrawerStep;
  soon?: boolean;
  premium?: boolean;
}
const apps: App[] = [
  {
    id: 'market-widget',
    title: 'Import market widget',
    description: 'Imports widget from the existing market pages',
    icon: <DashboardOutlined />,
    category: Category.Markets,
    step: DrawerStep.SelectMarketWidget
  },
  {
    id: 'spot-chart',
    title: 'Spot chart',
    description: 'Creates custom Spot History and Seasonal charts',
    icon: <SimpleChartIcon />,
    category: Category.Charts,
    step: DrawerStep.SelectSpotChart,
    premium: true
  },
  {
    id: 'fwd-chart',
    title: 'Forward Curves chart',
    description: 'Creates custom Forward Curves charts, up to 2 curves',
    icon: <FWDChartIcon />,
    category: Category.Charts,
    step: DrawerStep.SelectFWDChart,
    premium: true
  },
  {
    id: 'basic-data-table',
    title: 'Basic data table',
    description: 'Creates your custom Data Table, up to 20 items',
    icon: <DataTableIcon />,
    category: Category.Data,
    step: DrawerStep.SelectBasicDataTable
  },
  {
    id: 'highlighted-price',
    title: 'Highlighted price',
    description: 'Creates highlighted price at the top, up to 8 items',
    icon: <HighlightPriceIcon />,
    category: Category.Data,
    step: DrawerStep.SelectHighlightedPrice
  }
];
const categories = [
  { key: Category.All, label: 'All apps' },
  { key: Category.Markets, label: 'Markets' },
  { key: Category.Charts, label: 'Charts' },
  { key: Category.Data, label: 'Data' }
];

export const AppSelector = () => {
  const { setDrawerStep, closeDrawer } = useDrawerSteps();
  const [category, setCategory] = useState<Category>(Category.All);

  const { isUserPremium } = useUserDetails();

  const filteredApps = useMemo(() => {
    if (category === Category.All) {
      return apps;
    }
    return apps.filter(app => app.category === category);
  }, [category]);

  const [availableApps, premiumOnlyApps] = useMemo(() => {
    if (isUserPremium) {
      return [filteredApps, [] as App[]];
    }
    const nonPremium = filteredApps.filter(app => !app.premium);
    const premium = filteredApps.filter(app => app.premium);
    return [nonPremium, premium];
  }, [filteredApps, isUserPremium]);

  return (
    <>
      <DrawerHeader
        before={
          <DrawerButton
            icon={<ArrowBackIcon />}
            title='Go back'
            variant='secondary'
            onClick={closeDrawer}
          />
        }
        buttons={null}
        title='Add new app'
      />
      <DrawerBody>
        <Typography
          size={24}
          sx={{ fontWeight: 'bold' }}
        >
          Choose app type
        </Typography>
        <Typography
          color='textDisabled'
          size={14}
          sx={{
            mb: 3
          }}
        >
          Select an app to start configuring
        </Typography>
        <Box sx={{ display: 'grid', gap: 1, mb: 3, gridTemplateColumns: 'repeat(4,1fr)' }}>
          {categories.map(({ key, label }) => (
            <Button
              key={key}
              color={category === key ? 'primary' : 'secondary'}
              size='small'
              sx={{
                width: 'auto',
                maxWidth: '100%',
                height: 36
              }}
              variant='contained'
              onClick={() => {
                setCategory(key);
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {premiumOnlyApps.length > 0 && (
            <Box sx={{ position: 'relative', overflow: 'hidden', userSelect: 'none' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  position: 'relative',
                  filter: 'blur(3px)'
                }}
              >
                {premiumOnlyApps.map(app => (
                  <SelectWidget
                    key={app.id}
                    description={app.description}
                    icon={app.icon}
                    soon={app.soon}
                    testId={`select-widget-${app.id}`}
                    title={app.title}
                    onSelect={() => {}}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  background: 'rgba(255, 255, 255, 0.05)',
                  cursor: 'default',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: '#FFF'
                }}
              >
                <Typography>This is a Premium feature</Typography>
                <Typography>
                  Upgrade to GX Go Premium to enable charts and unlock advanced functionality.
                </Typography>
              </Box>
            </Box>
          )}
          {availableApps.map(app => (
            <SelectWidget
              key={app.id}
              description={app.description}
              icon={app.icon}
              soon={app.soon}
              testId={`select-widget-${app.id}`}
              title={app.title}
              onSelect={() => {
                if (!app.step) return;
                setDrawerStep(app.step);
              }}
            />
          ))}
        </Box>
      </DrawerBody>
    </>
  );
};
